import { NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  Injector,
  InputSignal,
  OnInit,
  Optional,
  OutputEmitterRef,
  Signal,
  ViewChild,
  WritableSignal,
  afterNextRender,
  computed,
  input,
  linkedSignal,
  output,
  signal,
} from '@angular/core';

import { InsightsEvent, InsightsEventProperties, InsightsEventsTrackingService } from '@core/shared/util';
import {
  FilterClearButtonComponent,
  FilterGroupComponent,
  FilterSelectionInfoComponent,
  MaximizeListButtonComponent,
} from '@core/ui';
import { SingleSelectFacet, TreeFacet } from '@mp/shared/facets/domain';
import {
  FacetExpansionStateService,
  FacetSelectionService,
  flattenTreeFacetBucket,
  getSelectedNode,
  isSelected,
} from '@mp/shared/facets/util';

import { TreeFacetInsightsEventsConfig } from '../../models';

import { TreeFacetAutocompleteComponent } from './tree-facet-autocomplete/tree-facet-autocomplete.component';
import { TreeFacetBucketComponent } from './tree-facet-bucket/tree-facet-bucket.component';
import { TypeFacetValueSelectorComponent } from './type-facet-value-selector/type-facet-value-selector.component';

@Component({
  selector: 'mp-tree-facet',
  templateUrl: './tree-facet.component.html',
  styleUrl: './tree-facet.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgTemplateOutlet,

    FilterGroupComponent,
    FilterClearButtonComponent,
    FilterSelectionInfoComponent,
    TypeFacetValueSelectorComponent,
    TreeFacetAutocompleteComponent,
    TreeFacetBucketComponent,
    MaximizeListButtonComponent,
  ],
  providers: [FacetExpansionStateService],
})
export class TreeFacetComponent<TData extends object | unknown = unknown> implements OnInit {
  /**
   * The TreeFacet object to display.
   */
  readonly facet: InputSignal<TreeFacet<TData>> = input.required<TreeFacet<TData>>();

  /**
   * An optional facet with selectable types.
   */
  readonly typeFacet: InputSignal<SingleSelectFacet | null | undefined> = input<SingleSelectFacet | null | undefined>();

  /**
   * Specifies the title of the filter component.
   */
  readonly filterName: InputSignal<string | undefined> = input<string>();

  /**
   * Specifies the icon of the filter component.
   */
  readonly icon: InputSignal<string | undefined> = input<string>();

  /**
   * If the current selection state should be stored, provide a unique key within the current instance
   * of `FacetSelectionService`. This is a constant value and must not be changed.
   */
  readonly selectionKey: InputSignal<string | undefined> = input<string>();

  /**
   * The placeholder of the search field.
   */
  readonly searchFieldPlaceholder: InputSignal<string> = input<string>('Merkmale durchsuchen');

  readonly noBucketsInfoText: InputSignal<string> = input<string>('Keine Warengruppen gefunden');

  readonly scrollable: InputSignal<boolean> = input<boolean>(true);

  readonly showMaximizeButton: InputSignal<boolean> = input<boolean>(false);

  protected readonly isMaximized: WritableSignal<boolean> = signal(false);

  protected readonly isScrollable: Signal<boolean> = computed(() => {
    if (this.showMaximizeButton()) {
      return !this.isMaximized();
    }

    return this.scrollable();
  });

  readonly insightsEventsConfig: InputSignal<TreeFacetInsightsEventsConfig | undefined> =
    input<TreeFacetInsightsEventsConfig>();

  /**
   * Emits on selection change and provides the selected values of the facet.
   */
  readonly changed: OutputEmitterRef<string | undefined> = output<string | undefined>();

  /**
   * Emits on change of the selected tree type and provides the selected value of the type facet.
   */
  readonly changedType: OutputEmitterRef<string | undefined> = output<string | undefined>();

  @ViewChild('treeFacetAutocomplete')
  protected treeFacetAutocomplete?: TreeFacetAutocompleteComponent<TData>;

  protected readonly allBuckets: Signal<readonly TreeFacet.Bucket<TData>[]> = computed(() =>
    Array.from(flattenTreeFacetBucket(this.facet().root)).slice(1),
  );

  protected readonly selectedBucket: WritableSignal<TreeFacet.Bucket<TData> | undefined> = linkedSignal(
    () => getSelectedNode(this.facet()) as TreeFacet.Bucket<TData> | undefined,
  );

  protected readonly selectedTypeBucket: WritableSignal<SingleSelectFacet.Bucket | undefined> = linkedSignal(() =>
    this.typeFacet()?.buckets.find(isSelected),
  );

  protected readonly bucketsExpansionState: Signal<FacetSelectionService.SelectionState | undefined> = computed(() =>
    this.getBucketsExpansionState(),
  );

  constructor(
    private readonly selectionService: FacetSelectionService,
    private readonly facetExpansionStateService: FacetExpansionStateService,
    private readonly injector: Injector,
    @Optional() private readonly insightsEventsTrackingService: InsightsEventsTrackingService | null,
  ) {}

  ngOnInit(): void {
    this.facetExpansionStateService.setSelectionKey(this.selectionKey());
  }

  get isExpanded(): boolean {
    return this.facetExpansionStateService.isExpanded();
  }

  onExpandedChange(expanded: boolean): void {
    this.facetExpansionStateService.setExpanded(expanded);

    const insightsEvent: string | undefined = this.isExpanded
      ? this.insightsEventsConfig()?.filterExpand
      : this.insightsEventsConfig()?.filterCollapse;
    this.trackInsightsEvent(insightsEvent);
  }

  get isSearchExpanded(): boolean {
    return this.facetExpansionStateService.isSearchExpanded();
  }

  setSearchExpanded(expanded: boolean): void {
    this.facetExpansionStateService.setSearchExpanded(expanded);

    if (expanded) {
      afterNextRender({ read: () => this.treeFacetAutocomplete?.focusSearchField() }, { injector: this.injector });
    }
  }

  isBucketExpanded(bucket: TreeFacet.Bucket<TData>): boolean {
    return this.bucketsExpansionState()?.isSelected(bucket.value) ?? false;
  }

  onBucketExpandedChange(bucket: TreeFacet.Bucket<TData>, expanded: boolean): void {
    this.bucketsExpansionState()?.toggleSelected(bucket.value, expanded);
  }

  setSelectedBucket(bucket: TreeFacet.Bucket<TData>): void {
    this.selectedBucket.set(bucket);

    this.trackInsightsEvent(this.insightsEventsConfig()?.valueSelect, { value: bucket.label });

    this.changed.emit(bucket.value);
  }

  handleSearchOptionClick(bucket: TreeFacet.Bucket<TData>): void {
    this.setSearchExpanded(false);
    this.setSelectedBucket(bucket);
  }

  onSearchFieldUse(): void {
    this.trackInsightsEvent(this.insightsEventsConfig()?.filterSearch);
  }

  handleTypeChange(bucket: SingleSelectFacet.Bucket): void {
    this.selectedTypeBucket.set(bucket);

    this.trackInsightsEvent(this.insightsEventsConfig()?.valueTypeSelect, { value: bucket.label });

    this.changedType.emit(bucket.value);
  }

  clearSelection(): void {
    this.selectedBucket.set(undefined);

    this.trackInsightsEvent(this.insightsEventsConfig()?.filterReset);

    this.changed.emit(undefined);
  }

  private getBucketsExpansionState(): FacetSelectionService.SelectionState | undefined {
    const selectionKey: string | undefined = this.selectionKey();

    if (!selectionKey) {
      return undefined;
    }

    const bucketsExpansionState: FacetSelectionService.SelectionState<string> =
      this.selectionService.getSelectionState(selectionKey);
    this.setBucketsInitialExpansion(bucketsExpansionState, this.allBuckets());

    return bucketsExpansionState;
  }

  private setBucketsInitialExpansion(
    selectionState: FacetSelectionService.SelectionState,
    allBuckets: readonly TreeFacet.Bucket<TData>[],
  ): void {
    for (const b of allBuckets) {
      // Expand buckets only when they are initially set
      if (b.selected && !selectionState.isSet(b.value)) {
        selectionState.toggleSelected(b.value, true);
      }
    }
  }

  private trackInsightsEvent(event: InsightsEvent | undefined, customProperties?: InsightsEventProperties): void {
    if (!event) {
      return;
    }

    this.insightsEventsTrackingService?.trackEvent(event, customProperties);
  }
}
